import { cn } from "../lib/utils";
import { type ButtonHTMLAttributes, forwardRef } from "react";

type CTAVariant = "primary" | "secondary" | "outline" | "hero";

interface CTAButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: CTAVariant;
  size?: "sm" | "md" | "lg" | "xl";
  fullWidth?: boolean;
}

const variantClasses: Record<CTAVariant, string> = {
  primary:
    "bg-gradient-accent text-foreground font-semibold tracking-wide shadow-gold hover:brightness-110 hover:shadow-gold active:scale-[0.98]",
  secondary:
    "bg-primary text-primary-foreground font-semibold tracking-wide hover:bg-primary/90 active:scale-[0.98]",
  outline:
    "border-2 border-gold text-gold bg-transparent font-semibold tracking-wide hover:bg-accent/10 active:scale-[0.98]",
  hero: "bg-gradient-accent text-foreground font-bold tracking-widest uppercase shadow-gold hover:brightness-110 hover:shadow-gold active:scale-[0.98] text-sm",
};

const sizeClasses = {
  sm: "px-4 py-2 text-sm rounded-md",
  md: "px-6 py-3 text-base rounded-lg",
  lg: "px-8 py-4 text-base rounded-lg",
  xl: "px-10 py-5 text-lg rounded-lg",
};

const CTAButton = forwardRef<HTMLButtonElement, CTAButtonProps>(
  (
    {
      variant = "primary",
      size = "md",
      fullWidth = false,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        className={cn(
          "inline-flex items-center justify-center gap-2 transition-smooth cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed",
          variantClasses[variant],
          sizeClasses[size],
          fullWidth && "w-full",
          className,
        )}
        {...props}
      >
        {children}
      </button>
    );
  },
);

CTAButton.displayName = "CTAButton";

export { CTAButton };
export type { CTAVariant };
